import {
  MeasureKind, MeasureVM, Pt, SnapHit, ViewMap,
  bboxOfPts, dist, polygonArea, polygonCentroid, polylineLength, rectOutline,
} from './dwgMeasure';

const NS = 'http://www.w3.org/2000/svg';

// Two palettes: the drawing background is either near-black (#101418) or light
// paper (#efebe7, the default) — bright emerald/amber strokes wash out on the
// latter. The label pills stay dark slate, readable on both.
type Theme = {
  stroke: string; strokeHl: string; fill: string;
  draftStroke: string; draftFill: string;
  snap: string; handleFill: string;
};

const THEME_DARK: Theme = {
  stroke: '#34d399', strokeHl: '#6ee7b7', fill: 'rgba(52, 211, 153, 0.13)',
  draftStroke: '#fbbf24', draftFill: 'rgba(251, 191, 36, 0.10)',
  snap: '#38bdf8', handleFill: '#0f172a',
};

const THEME_LIGHT: Theme = {
  stroke: '#047857', strokeHl: '#065f46', fill: 'rgba(4, 120, 87, 0.12)',
  draftStroke: '#b45309', draftFill: 'rgba(180, 83, 9, 0.10)',
  snap: '#0369a1', handleFill: '#ffffff',
};

const PILL_BG = 'rgba(15, 23, 42, 0.92)';
const PILL_TEXT = '#e2e8f0';
const PILL_BORDER = 'rgba(52, 211, 153, 0.45)';

type Formatters = {
  len: (svgUnits: number) => string;
  area: (svgUnits2: number) => string;
};

type ScreenItem = {
  el: SVGGElement;
  docX: number;
  docY: number;
  offX: number;
  offY: number;
};

type Rendered = {
  outline: SVGPathElement;
  fillable: boolean;
};

function svgEl<T extends SVGElement>(tag: string, attrs: Record<string, string>): T {
  const el = document.createElementNS(NS, tag) as unknown as T;
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

function pathOf(pts: Pt[], close: boolean): string {
  if (!pts.length) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) d += ` L ${pts[i].x} ${pts[i].y}`;
  return close ? d + ' Z' : d;
}

export class DWGMeasureOverlay {
  private root: SVGSVGElement;
  private gWorld: SVGGElement;
  private gScreen: SVGGElement;
  private gDraftWorld: SVGGElement;
  private gDraftScreen: SVGGElement;
  private gSnap: SVGGElement;

  private map: ViewMap = { tx: 0, ty: 0, k: 1 };
  private fmt: Formatters = { len: (v) => String(v), area: (v) => String(v) };
  private theme: Theme = THEME_LIGHT;
  private items: ScreenItem[] = [];
  private rendered = new Map<string, Rendered>();
  private measurements: MeasureVM[] = [];
  private highlightId: string | null = null;
  private snapHit: SnapHit | null = null;
  private idPins: { doc: Pt; label: string }[] = [];

  private draftKind: MeasureKind | null = null;
  private draftPts: Pt[] = [];
  private cursor: Pt | null = null;

  onDraftComplete: ((kind: MeasureKind, pts: Pt[]) => void) | null = null;

  constructor(root: SVGSVGElement) {
    this.root = root;
    this.gWorld = svgEl<SVGGElement>('g', {});
    this.gScreen = svgEl<SVGGElement>('g', {});
    this.gDraftWorld = svgEl<SVGGElement>('g', {});
    this.gDraftScreen = svgEl<SVGGElement>('g', {});
    this.gSnap = svgEl<SVGGElement>('g', {});
    root.appendChild(this.gWorld);
    root.appendChild(this.gDraftWorld);
    root.appendChild(this.gScreen);
    root.appendChild(this.gDraftScreen);
    root.appendChild(this.gSnap);
  }

  destroy(): void {
    this.root.innerHTML = '';
  }

  toScreen(p: Pt): Pt {
    return { x: this.map.tx + this.map.k * p.x, y: this.map.ty + this.map.k * p.y };
  }

  toDoc(p: Pt): Pt {
    return { x: (p.x - this.map.tx) / this.map.k, y: (p.y - this.map.ty) / this.map.k };
  }

  setView(map: ViewMap): void {
    this.map = map;
    const t = `translate(${map.tx} ${map.ty}) scale(${map.k})`;
    this.gWorld.setAttribute('transform', t);
    this.gDraftWorld.setAttribute('transform', t);
    for (const it of this.items) {
      const x = map.tx + map.k * it.docX + it.offX;
      const y = map.ty + map.k * it.docY + it.offY;
      it.el.setAttribute('transform', `translate(${x} ${y})`);
    }
    if (this.snapHit) {
      const s = this.toScreen(this.snapHit);
      const g = this.gSnap.firstElementChild;
      if (g) g.setAttribute('transform', `translate(${s.x} ${s.y})`);
    }
    this.renderDraft();
  }

  setTheme(light: boolean): void {
    const next = light ? THEME_LIGHT : THEME_DARK;
    if (next === this.theme) return;
    this.theme = next;
    this.rebuild();
    this.renderDraft();
    this.setSnapMarker(this.snapHit);
  }

  setFormatters(fmt: Formatters): void {
    this.fmt = fmt;
    this.rebuild();
  }

  setMeasurements(list: MeasureVM[]): void {
    this.measurements = list;
    this.rebuild();
  }

  setHighlight(id: string | null): void {
    if (this.highlightId === id) return;
    this.highlightId = id;
    for (const [mid, r] of this.rendered) {
      const hl = mid === id;
      r.outline.setAttribute('stroke', hl ? this.theme.strokeHl : this.theme.stroke);
      r.outline.setAttribute('stroke-width', hl ? '3.5' : '2');
    }
  }

  // ── Draft ───────────────────────────────────────────────────────────────────

  startTool(kind: MeasureKind | null): void {
    this.draftKind = kind;
    this.draftPts = [];
    this.cursor = null;
    this.renderDraft();
    if (!kind) this.setSnapMarker(null);
  }

  hasDraft(): boolean {
    return this.draftPts.length > 0;
  }

  draftPointCount(): number {
    return this.draftPts.length;
  }

  updateCursor(doc: Pt | null): void {
    this.cursor = doc;
    this.renderDraft();
  }

  lastDraftPoint(): Pt | null {
    return this.draftPts.length ? this.draftPts[this.draftPts.length - 1] : null;
  }

  firstDraftPoint(): Pt | null {
    return this.draftPts.length ? this.draftPts[0] : null;
  }

  addDraftPoint(doc: Pt): 'continue' | 'complete' | 'ignored' {
    if (!this.draftKind) return 'ignored';
    const last = this.lastDraftPoint();
    if (last && dist(last, doc) * this.map.k < 1) return 'ignored';

    if (this.draftKind === 'area' && this.draftPts.length >= 3) {
      const firstScreen = this.toScreen(this.draftPts[0]);
      const clickScreen = this.toScreen(doc);
      if (dist(firstScreen, clickScreen) < 12) {
        this.completeDraft();
        return 'complete';
      }
    }

    this.draftPts.push(doc);
    if ((this.draftKind === 'line' || this.draftKind === 'rect') && this.draftPts.length === 2) {
      this.completeDraft();
      return 'complete';
    }
    this.renderDraft();
    return 'continue';
  }

  finishDraft(): boolean {
    const min = this.draftKind === 'area' ? 3 : 2;
    const pts = this.trimmedDraft(min);
    if (!this.draftKind || pts.length < min) return false;
    this.draftPts = pts;
    this.completeDraft();
    return true;
  }

  cancelDraft(): void {
    this.draftPts = [];
    this.cursor = null;
    this.renderDraft();
  }

  // Drop only TRAILING near-duplicates: the second click of a finishing
  // double-click lands within OS slop (~5 px) of the last real vertex. Interior
  // points are never touched — they were validated at their own zoom level, and
  // re-checking them at the current zoom would collapse legitimate vertices
  // after zooming out mid-draft.
  private trimmedDraft(min: number): Pt[] {
    const pts = [...this.draftPts];
    while (pts.length > min && dist(pts[pts.length - 1], pts[pts.length - 2]) * this.map.k < 6) {
      pts.pop();
    }
    return pts;
  }

  private completeDraft(): void {
    const kind = this.draftKind!;
    const pts = this.draftPts;
    this.draftPts = [];
    this.cursor = null;
    this.renderDraft();
    const min = kind === 'area' ? 3 : 2;
    if (pts.length >= min && this.onDraftComplete) this.onDraftComplete(kind, pts);
  }

  setSnapMarker(hit: SnapHit | null): void {
    this.snapHit = hit;
    this.gSnap.innerHTML = '';
    if (!hit) return;
    const s = this.toScreen(hit);
    const g = svgEl<SVGGElement>('g', { transform: `translate(${s.x} ${s.y})` });
    const common = { fill: 'none', stroke: this.theme.snap, 'stroke-width': '1.8' };
    if (hit.type === 'vertex') {
      g.appendChild(svgEl('rect', { ...common, x: '-5', y: '-5', width: '10', height: '10' }));
    } else if (hit.type === 'midpoint') {
      g.appendChild(svgEl('path', { ...common, d: 'M 0 -6 L 6 5 L -6 5 Z' }));
    } else if (hit.type === 'intersection') {
      g.appendChild(svgEl('path', { ...common, d: 'M -5 -5 L 5 5 M -5 5 L 5 -5' }));
    } else if (hit.type === 'survey') {
      g.appendChild(svgEl('circle', { ...common, r: '7' }));
      g.appendChild(svgEl('path', { ...common, d: 'M -10 0 L 10 0 M 0 -10 L 0 10' }));
    } else {
      g.appendChild(svgEl('circle', { ...common, r: '4.5' }));
    }
    this.gSnap.appendChild(g);
  }

  measurementBBox(id: string): { minX: number; minY: number; maxX: number; maxY: number } | null {
    const m = this.measurements.find((x) => x.id === id);
    if (!m || !m.pts.length) return null;
    const pts = m.kind === 'rect' && m.pts.length >= 2 ? rectOutline(m.pts[0], m.pts[1]) : m.pts;
    return bboxOfPts(pts);
  }

  // ── Rendering ───────────────────────────────────────────────────────────────

  private rebuild(): void {
    this.gWorld.innerHTML = '';
    this.gScreen.innerHTML = '';
    this.items = [];
    this.rendered.clear();
    this.setView(this.map);
    for (const m of this.measurements) this.renderMeasurement(m);
    for (const pin of this.idPins) this.renderIdPin(pin);
    this.setView(this.map);
  }

  setIdPins(pins: { doc: Pt; label: string }[]): void {
    this.idPins = pins;
    this.rebuild();
  }

  private renderIdPin(pin: { doc: Pt; label: string }): void {
    const dot = svgEl<SVGGElement>('g', {});
    dot.appendChild(svgEl('circle', {
      r: '4', fill: this.theme.snap, stroke: this.theme.handleFill, 'stroke-width': '1.5',
    }));
    this.addScreenItem(dot, pin.doc, 0, 0);
    this.addScreenItem(this.pill(pin.label), pin.doc, 0, -24);
  }

  private addScreenItem(el: SVGGElement, doc: Pt, offX: number, offY: number): void {
    this.items.push({ el, docX: doc.x, docY: doc.y, offX, offY });
    this.gScreen.appendChild(el);
  }

  private pill(text: string, opts?: { dim?: boolean }): SVGGElement {
    const g = svgEl<SVGGElement>('g', {});
    const rect = svgEl<SVGRectElement>('rect', {
      rx: '4', fill: PILL_BG, stroke: opts?.dim ? 'rgba(148,163,184,0.4)' : PILL_BORDER, 'stroke-width': '1',
    });
    const t = svgEl<SVGTextElement>('text', {
      fill: PILL_TEXT,
      'font-size': '11',
      'font-family': "Inter, 'Plus Jakarta Sans', sans-serif",
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    });
    t.textContent = text;
    g.appendChild(rect);
    g.appendChild(t);
    this.root.appendChild(g);
    const tl = t.getComputedTextLength();
    const w = tl + 14, h = 20;
    rect.setAttribute('x', String(-w / 2));
    rect.setAttribute('y', String(-h / 2));
    rect.setAttribute('width', String(w));
    rect.setAttribute('height', String(h));
    g.remove();
    return g;
  }

  private handleDot(doc: Pt, draft: boolean): void {
    const g = svgEl<SVGGElement>('g', {});
    g.appendChild(svgEl('circle', {
      r: '3', fill: this.theme.handleFill, stroke: draft ? this.theme.draftStroke : this.theme.stroke, 'stroke-width': '1.5',
    }));
    if (draft) {
      this.gDraftScreen.appendChild(g);
      const s = this.toScreen(doc);
      g.setAttribute('transform', `translate(${s.x} ${s.y})`);
    } else {
      this.addScreenItem(g, doc, 0, 0);
    }
  }

  private labelFor(m: MeasureVM): { text: string; anchor: Pt; offX: number; offY: number } {
    const name = m.name ? `${m.name} — ` : '';
    if (m.kind === 'line') {
      const [a, b] = [m.pts[0], m.pts[1]];
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const dx = b.x - a.x, dy = b.y - a.y;
      const l = Math.hypot(dx, dy) || 1;
      return {
        text: `${name}${this.fmt.len(polylineLength(m.pts))}`,
        anchor: mid,
        offX: (-dy / l) * 16,
        offY: (dx / l) * 16,
      };
    }
    if (m.kind === 'polyline') {
      const mid = m.pts[Math.floor(m.pts.length / 2)];
      return { text: `${name}${this.fmt.len(polylineLength(m.pts))}`, anchor: mid, offX: 0, offY: -18 };
    }
    if (m.kind === 'rect') {
      const [a, b] = [m.pts[0], m.pts[1]];
      const w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
      return {
        text: `${name}${this.fmt.len(w)} × ${this.fmt.len(h)} · ${this.fmt.area(w * h)}`,
        anchor: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        offX: 0, offY: 0,
      };
    }
    return {
      text: `${name}${this.fmt.area(polygonArea(m.pts))}`,
      anchor: polygonCentroid(m.pts),
      offX: 0, offY: 0,
    };
  }

  private renderMeasurement(m: MeasureVM): void {
    if (m.pts.length < 2) return;
    const closed = m.kind === 'rect' || m.kind === 'area';
    const outlinePts = m.kind === 'rect' ? rectOutline(m.pts[0], m.pts[1]) : m.pts;
    const hl = m.id === this.highlightId;
    const path = svgEl<SVGPathElement>('path', {
      d: pathOf(outlinePts, closed),
      fill: closed ? this.theme.fill : 'none',
      stroke: hl ? this.theme.strokeHl : this.theme.stroke,
      'stroke-width': hl ? '3.5' : '2',
      'vector-effect': 'non-scaling-stroke',
      'stroke-linejoin': 'round',
      'stroke-linecap': 'round',
      opacity: m.unsaved ? '0.55' : '1',
    });
    this.gWorld.appendChild(path);
    this.rendered.set(m.id, { outline: path, fillable: closed });

    for (const p of outlinePts) this.handleDot(p, false);

    const lb = this.labelFor(m);
    const pill = this.pill(lb.text, { dim: !!m.unsaved });
    this.addScreenItem(pill, lb.anchor, lb.offX, lb.offY);
  }

  private renderDraft(): void {
    this.gDraftWorld.innerHTML = '';
    this.gDraftScreen.innerHTML = '';
    if (!this.draftKind || (!this.draftPts.length && !this.cursor)) return;

    const kind = this.draftKind;
    const pts = this.draftPts;
    const cur = this.cursor;

    let outline: Pt[] = pts;
    let closed = false;
    if (kind === 'rect' && pts.length === 1 && cur) {
      outline = rectOutline(pts[0], cur);
      closed = true;
    } else if (cur && pts.length) {
      outline = [...pts, cur];
      closed = kind === 'area' && outline.length >= 3;
    } else if (kind === 'area' && pts.length >= 3) {
      closed = true;
    }

    if (outline.length >= 2) {
      this.gDraftWorld.appendChild(svgEl('path', {
        d: pathOf(outline, closed),
        fill: closed ? this.theme.draftFill : 'none',
        stroke: this.theme.draftStroke,
        'stroke-width': '2',
        'stroke-dasharray': '5 4',
        'vector-effect': 'non-scaling-stroke',
        'stroke-linejoin': 'round',
        'stroke-linecap': 'round',
      }));
    }

    for (const p of pts) this.handleDot(p, true);

    if (cur && pts.length) {
      const text = this.draftLabel(kind, pts, cur);
      if (text) {
        const pill = this.pill(text);
        const s = this.toScreen(cur);
        pill.setAttribute('transform', `translate(${s.x + 24} ${s.y + 22})`);
        this.gDraftScreen.appendChild(pill);
      }
    }
  }

  private draftLabel(kind: MeasureKind, pts: Pt[], cur: Pt): string {
    if (kind === 'line') return this.fmt.len(dist(pts[0], cur));
    if (kind === 'rect') {
      const w = Math.abs(cur.x - pts[0].x), h = Math.abs(cur.y - pts[0].y);
      return `${this.fmt.len(w)} × ${this.fmt.len(h)} · ${this.fmt.area(w * h)}`;
    }
    if (kind === 'polyline') {
      const seg = dist(pts[pts.length - 1], cur);
      const total = polylineLength(pts) + seg;
      return pts.length > 1
        ? `${this.fmt.len(seg)} · Σ ${this.fmt.len(total)}`
        : this.fmt.len(seg);
    }
    const poly = [...pts, cur];
    if (poly.length < 3) return this.fmt.len(dist(pts[pts.length - 1], cur));
    return this.fmt.area(polygonArea(poly));
  }
}
